import { useMemo } from 'react'

import { styled } from 'styled-components'

import {
  UnstyledShippingDestinationAddress,
  WeightInPounds,
} from '@b-stock/fe-core/bstock-next/components'
import { formatAddressShort } from '@b-stock/fe-core/bstock-next/helpers'
import type {
  OrchestratedListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  FormattedMessage,
  SpinnerDots,
  InteractiveIcon,
  Tag,
  TagType,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'
import type { GroupDtoTypeEnum } from '@b-stock/listing-api-client'
import { ShipmentType } from '@b-stock/shipment-api-client'

import { ChangeButton, Section, SectionCard, SectionTitle } from '../AutoPay.ui'

const ns = 'BidConfirmationModal'

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const ShippingContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`

const ShippingValue = styled.span`
  ${Typography.Subtitle1}
  color: ${Colors.Semantic.Neutral.light_black};
`

const ShippingDestinationText = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.light_black};
`

const ShippingSubtext = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  white-space: pre-line;
`

const ShipmentDetailsContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`

const ShipmentDetailsTitle = styled.span`
  ${Typography.Subtitle2}
  color: ${Colors.Semantic.Neutral.light_black};
`

const ShipmentDetailsText = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
`

const StyledInteractiveIcon = styled(InteractiveIcon)`
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  font-size: 0.875rem;
`

export type AutoPayShippingSummaryProps = {
  selectedQuote: OrchestratedQuote | null | undefined
  onShippingChangeClick?: () => void
  shippingChangePending?: boolean
  groups?: OrchestratedListingDetails['lot']['groups']
  onCarrierChangeClick?: () => void
  isCarrierChangeLoading?: boolean
  isCarrierChangeDisabled?: boolean
  hasMultipleCarrierOptions?: boolean
  isCheapestCarrier?: boolean
}

export const AutoPayShippingSummary = ({
  selectedQuote,
  onShippingChangeClick,
  shippingChangePending,
  groups,
  onCarrierChangeClick,
  isCarrierChangeLoading,
  isCarrierChangeDisabled,
  hasMultipleCarrierOptions,
  isCheapestCarrier,
}: AutoPayShippingSummaryProps) => {
  const { gWeight, unitCount, groupType } = useMemo(() => {
    let gWeight = 0
    let unitCount = 0
    let groupType: GroupDtoTypeEnum | 'MIXED' | undefined
    for (const group of groups ?? []) {
      if (groupType && group.type !== groupType) {
        groupType = 'MIXED'
      } else {
        groupType = group.type
      }
      if (group.dimensions) {
        for (const dim of group.dimensions) {
          gWeight += dim.weight?.gValue ?? 0
          unitCount += dim.count ?? 1
        }
      }
    }
    return { gWeight, unitCount, groupType: groupType ?? 'UNKNOWN' }
  }, [groups])

  if (!selectedQuote) return null

  const isBuyerPickup = selectedQuote.shipmentType === ShipmentType.BuyerPickup
  const isPickupTypeShipment =
    isBuyerPickup || selectedQuote.shipmentType === ShipmentType.BuyerArranged

  return (
    <Section>
      <SectionTitleRow>
        <SectionTitle>
          <FormattedMessage
            id={
              isPickupTypeShipment
                ? `${ns}.shipping.title.pickup`
                : `${ns}.shipping.title`
            }
          />
        </SectionTitle>
        {isPickupTypeShipment && (
          <Tag type={TagType.info}>
            <FormattedMessage
              id={
                isBuyerPickup
                  ? 'TotalPaymentInfo.shippingTags.BUYER_PICKUP'
                  : 'TotalPaymentInfo.shippingTags.BUYER_ARRANGED'
              }
            />
          </Tag>
        )}
      </SectionTitleRow>
      <SectionCard>
        <ShippingContent>
          <ShippingValue>
            <UnstyledShippingDestinationAddress quote={selectedQuote} />
          </ShippingValue>
          {isPickupTypeShipment && (
            <ShippingDestinationText>
              <FormattedMessage
                id={`${ns}.shipping.destination`}
                values={{
                  address: formatAddressShort(selectedQuote.destination),
                }}
              />
            </ShippingDestinationText>
          )}
          <ShippingSubtext>
            <FormattedMessage
              id={
                isPickupTypeShipment
                  ? `${ns}.shipping.subtext.pickup`
                  : `${ns}.shipping.subtext`
              }
            />
          </ShippingSubtext>
        </ShippingContent>
        {onShippingChangeClick && (
          <ChangeButton
            appearance={shippingChangePending ? 'loading' : 'secondary'}
            size="small"
            type="button"
            onClick={onShippingChangeClick}
            disabled={shippingChangePending}
          >
            <FormattedMessage id={`${ns}.shipping.change`} />
          </ChangeButton>
        )}
      </SectionCard>
      {groups && (
        <SectionCard>
          <ShipmentDetailsContent>
            <ShipmentDetailsTitle>
              <FormattedMessage id={`${ns}.shipping.shipmentDetails`} />
            </ShipmentDetailsTitle>
            <ShipmentDetailsText>
              <FormattedMessage
                id="Auction.details.receiveGoods.shipmentDetail"
                values={{
                  formattedWeight: <WeightInPounds grams={gWeight} />,
                  byUnits: (
                    <FormattedMessage
                      id={`Auction.details.receiveGoods.shipmentDetailByUnits.${groupType}`}
                      values={{ unitCount }}
                      defaultMessage=" "
                    />
                  ),
                }}
              />
            </ShipmentDetailsText>
            {selectedQuote.carrier?.name && (
              <ShipmentDetailsText>
                <FormattedMessage
                  id="Auction.details.receiveGoods.carrier"
                  values={{ carrierName: selectedQuote.carrier.name }}
                />
                {onCarrierChangeClick && !hasMultipleCarrierOptions && (
                  <span data-testid="single-quote-info-icon">
                    <StyledInteractiveIcon
                      tooltipProps={{ placement: 'bottom' }}
                    >
                      <FormattedMessage id="ChangeCarrierModal.singleQuoteInfo" />
                    </StyledInteractiveIcon>
                  </span>
                )}
                {isCheapestCarrier && (
                  <>
                    {' '}
                    <FormattedMessage id="BStockNext.CarrierPickerDropdown.cheapest" />
                  </>
                )}
              </ShipmentDetailsText>
            )}
          </ShipmentDetailsContent>
          {onCarrierChangeClick &&
            hasMultipleCarrierOptions &&
            (isCarrierChangeLoading ? (
              <SpinnerDots />
            ) : (
              <ChangeButton
                appearance="secondary"
                size="small"
                type="button"
                onClick={onCarrierChangeClick}
                disabled={isCarrierChangeDisabled}
              >
                <FormattedMessage id="Common.changeCarrier" />
              </ChangeButton>
            ))}
        </SectionCard>
      )}
    </Section>
  )
}
